import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { drawReportHeader } from "./headerUtil";
import { themeColors } from "./themeColors";

const hexToRgb = (hex) => {
    if (!hex) return [0, 0, 0];
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
};

const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    if (months < 0) {
        years--;
        months += 12;
    }
    return `${years}Y ${months}M`;
};

const formatDateTime = (datetimeStr) => {
    if (!datetimeStr) return "-";
    const date = new Date(datetimeStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
};

/**
 * Reusable utility to generate Prescription PDF for OPD, IPD, and History
 * @param {Object} data - Contains patientData, medicines, checkedData, authCtx
 */
export const exportPrescriptionPDF = (data) => {
    const {
        patientData,
        medicines,
        checkedData,
        authCtx,
        selectedDateTime,
        isHistory = false,
        reportTitle = "PRESCRIPTION"
    } = data;

    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const lineSpacing = 6;

    // 1. Draw Standardized Header (Full Width, Top of page, No extra title/line)
    let currentY = drawReportHeader(doc, authCtx?.hospitalData || authCtx, "", {
        margin: 0,
        top: 0,
        contentMargin: margin,
        hideSeparator: true,
        isExport: true
    });

    // 2. Patient Information
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");

    // Normalize Patient Data
    // Check if patientData has a nested patientId OBJECT, otherwise use patientData as the patient object
    const pIdObj = (patientData?.patientId && typeof patientData.patientId === 'object')
        ? patientData.patientId
        : patientData;

    const patientName = `${pIdObj.firstName || pIdObj.name || pIdObj.patientName || ""} ${pIdObj.lastName || ""}`.trim().toUpperCase();

    // Improved Gender logic
    const rawGender = (pIdObj.gender || "-").toUpperCase();
    const gender = rawGender.startsWith("M") ? "male" : rawGender.startsWith("F") ? "female" : (pIdObj.gender || "-");

    // Improved Age logic - use age if dob is missing or invalid
    let age = "-";
    if (pIdObj.dob && pIdObj.dob !== "-") {
        age = calculateAge(pIdObj.dob) || (pIdObj.age ? `${pIdObj.age}Y` : "-");
    } else {
        age = pIdObj.age ? (String(pIdObj.age).includes("Y") ? pIdObj.age : `${pIdObj.age} Y`) : "-";
    }

    const patientId = pIdObj.patientId || patientData?.ipdAdmissionId || "-";
    const mobile = pIdObj.mobile || pIdObj.phone || "-";
    const displayDate = formatDateTime(checkedData?.billingDate || patientData?.dateTime || selectedDateTime || new Date());

    // Row 1: Name (Left) | Mobile (Right)
    doc.setFont("helvetica", "bold");
    doc.text("Name:", margin, currentY);
    let nameLabWidth = doc.getTextWidth("Name:");
    doc.setFont("helvetica", "normal");
    doc.text(` ${patientName} (${age}/${gender})`, margin + nameLabWidth, currentY);

    const mobileVal = ` ${mobile}`;
    doc.setFont("helvetica", "normal");
    doc.text(mobileVal, pageWidth - margin, currentY, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text("Mobile:", pageWidth - margin - doc.getTextWidth(mobileVal), currentY, { align: "right" });

    currentY += 6;

    // Row 2: Patient Id (Left) | OPD ID (Right)
    doc.setFont("helvetica", "bold");
    doc.text("Patient Id:", margin, currentY);
    let pidLabWidth = doc.getTextWidth("Patient Id:");
    doc.setFont("helvetica", "normal");
    doc.text(` ${patientId}`, margin + pidLabWidth, currentY);

    const checkupId = patientData?.checkUpId || patientData?.checkupId || patientData?.id || "-";
    const opdIdVal = ` ${checkupId}`;
    doc.setFont("helvetica", "normal");
    doc.text(opdIdVal, pageWidth - margin, currentY, { align: "right" });
    doc.setFont("helvetica", "bold");
    const idLabel = reportTitle.includes("IPD") ? "IPD NO:" : "OPD NO:";
    doc.text(idLabel, pageWidth - margin - doc.getTextWidth(opdIdVal), currentY, { align: "right" });

    currentY += 6;

    // Row 3: Reg. NO (Left) | Date (Right)
    const diagnosis = checkedData?.diagnosis || patientData?.diagnosis;
    const isCasePaper = reportTitle === "Case Paper";

    doc.setFont("helvetica", "bold");
    doc.text("Reg. NO:", margin, currentY);
    let regLabWidth = pIdObj.registrationNo ? doc.getTextWidth("Reg. NO:") : 0;
    doc.setFont("helvetica", "normal");
    if (pIdObj.registrationNo) doc.text(` ${pIdObj.registrationNo}`, margin + regLabWidth, currentY);

    const dateVal = ` ${displayDate}`;
    doc.setFont("helvetica", "normal");
    doc.text(dateVal, pageWidth - margin, currentY, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text("Date:", pageWidth - margin - doc.getTextWidth(dateVal), currentY, { align: "right" });

    currentY += 6;

    if (!isCasePaper && diagnosis && diagnosis !== "-") {
        const diagVal = ` ${diagnosis}`;
        doc.setFont("helvetica", "normal");
        doc.text(diagVal, pageWidth - margin, currentY, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Diagnosis:", pageWidth - margin - doc.getTextWidth(diagVal), currentY, { align: "right" });
    }
    currentY += 6;

    // Always show vitals row if any of these exist, matching the print layout's detail level
    doc.setFont("helvetica", "normal");
    const weight = checkedData?.weight || patientData?.weight || "-";
    const bps = checkedData?.bps || patientData?.bp1 || patientData?.bps || "-";
    const bpd = checkedData?.bpd || patientData?.bp2 || patientData?.bpd || "";
    const temp = checkedData?.temp || patientData?.temperature || patientData?.temp || "-";
    const pulse = checkedData?.pulse || patientData?.pulse || "-";
    const spo2 = checkedData?.spo2 || patientData?.spo2 || "-";

    // Draw Vitals Row with bold labels
    const drawVitalChunk = (label, value, x) => {
        doc.setFont("helvetica", "bold");
        doc.text(label, x, currentY);
        const labelWidth = doc.getTextWidth(label);
        doc.setFont("helvetica", "normal");
        doc.text(` ${value}`, x + labelWidth, currentY);
        return x + labelWidth + doc.getTextWidth(` ${value}`) + 4; // Add spacing
    };

    let vitalX = margin;
    let hasVitals = false;

    if (weight !== "-") {
        vitalX = drawVitalChunk("Weight:", `${weight} kg,`, vitalX);
        hasVitals = true;
    }
    if (bps !== "-") {
        const bpDisplay = `${bps}/${bpd || "-"} mm Hg,`;
        vitalX = drawVitalChunk("BP:", bpDisplay, vitalX);
        hasVitals = true;
    }
    if (temp !== "-") {
        vitalX = drawVitalChunk("Temp:", `${temp} °F,`, vitalX);
        hasVitals = true;
    }
    if (pulse !== "-") {
        vitalX = drawVitalChunk("Pulse:", `${pulse} bpm,`, vitalX);
        hasVitals = true;
    }
    if (spo2 !== "-") {
        vitalX = drawVitalChunk("SpO2:", `${spo2}%`, vitalX);
        hasVitals = true;
    }

    if (checkedData?.lmp) {
        vitalX = margin;
        currentY += 6;
        vitalX = drawVitalChunk("LMP:", checkedData.lmp + (checkedData.edd ? "," : ""), vitalX);
        if (checkedData?.edd) vitalX = drawVitalChunk("EDD:", checkedData.edd, vitalX);
        hasVitals = true;
    }
    if (checkedData?.length || checkedData?.hc) {
        vitalX = margin;
        currentY += 6;
        if (checkedData?.length) vitalX = drawVitalChunk("Length:", checkedData.length + (checkedData.hc ? "," : ""), vitalX);
        if (checkedData?.hc) vitalX = drawVitalChunk("Head Circ.:", checkedData.hc, vitalX);
        hasVitals = true;
    }

    if (hasVitals) currentY += 7;

    const complaint = checkedData?.complaint || patientData?.complaint;
    if (!isCasePaper && complaint && complaint !== "-") {
        doc.setFont("helvetica", "bold");
        doc.text(`Complaint: ${complaint}`, margin, currentY);
        currentY += 7;
    }
    doc.setFont("helvetica", "normal");

    // 4. Medicine Table
    if (!isCasePaper && medicines && medicines.length > 0) {
        const tableBody = medicines.map((m, idx) => [
            idx + 1,
            m.type || m.medicineType || "-",
            m.medicineName || "-",
            m.dosage || "-",
            m.breakFastDose || "-",
            m.lunchDose || "-",
            m.dinnerDose || "-",
            m.days || "-",
            m.instruction || m.notes || "-"
        ]);

        const actualData = authCtx?.hospitalData || authCtx || {};
        const themeName = actualData.applicationThemeColour || 'TEAL';
        const theme = themeColors[themeName] || themeColors.TEAL;
        const primaryRgb = hexToRgb(theme.primary);

        doc.autoTable({
            startY: currentY,
            head: [["Sr.", "Type", "Medicine", "Dosage", "Brkfst", "Lunch", "Dinner", "Days", "Instr"]],
            body: tableBody,
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: [10, 10, 10] },
            headStyles: {
                fillColor: primaryRgb,
                textColor: [255, 255, 255],
                fontStyle: "bold",
                lineWidth: 0.1,
                lineColor: primaryRgb,
                halign: 'center'
            },
            columnStyles: {
                0: { halign: "center", cellWidth: 10 },
                1: { halign: "center", cellWidth: 15 },
                2: { fontStyle: 'bold' },
                3: { halign: "center", cellWidth: 15 },
                4: { halign: "center", cellWidth: 15 },
                5: { halign: "center", cellWidth: 15 },
                6: { halign: "center", cellWidth: 15 },
                7: { halign: "center", cellWidth: 12 },
            },
            margin: { left: margin, right: margin, bottom: 40 }
        });
        currentY = doc.lastAutoTable.finalY + 8;
    }

    const advice = checkedData?.advice || patientData?.advice || "-";
    const nextAppt = checkedData?.nextAppointment || checkedData?.next_appointment;
    const showNextAppt = nextAppt && nextAppt !== "-" && !isHistory;

    if (!isCasePaper && (advice !== "-" || showNextAppt)) {
        if (currentY > pageHeight - 30) {
            doc.addPage();
            currentY = 20;
        }

        doc.setFont("helvetica", "bold");
        doc.text("Advice:", margin, currentY);
        let adviceLabWidth = doc.getTextWidth("Advice:");
        doc.setFont("helvetica", "normal");
        const adviceVal = ` ${advice}  `;
        doc.text(adviceVal, margin + adviceLabWidth, currentY);
        const adviceValWidth = doc.getTextWidth(adviceVal);

        if (showNextAppt) {
            const nextDateVal = `${nextAppt}`;
            doc.setFont("helvetica", "normal");
            doc.text(nextDateVal, pageWidth - margin, currentY, { align: "right" });
            
            const nextLabel = "Next Appointment: ";
            doc.setFont("helvetica", "bold");
            doc.text(nextLabel, pageWidth - margin - doc.getTextWidth(nextDateVal), currentY, { align: "right" });
        }
        currentY += 10;
    }

    // 6. Footer Signature
    const signatureY = pageHeight - 30;
    doc.setDrawColor(0, 0, 0);
    doc.line(pageWidth - margin - 65, signatureY, pageWidth - margin, signatureY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Authority Sign / Doctor Sign", pageWidth - margin - 32.5, signatureY + 5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const userRole = authCtx?.role;
    const userName = authCtx?.userName;

    // Condition: Display name only if the logged-in user is a Doctor
    if (userRole === "ROLE_DOCTOR" && userName) {
        doc.text(userName, pageWidth - margin - 32.5, signatureY + 10, { align: "center" });
    }

    // 7. Save
    const suffix = isCasePaper ? "CasePaper" : "Prescription";
    const fileName = `${patientName || "Patient"}_${suffix}.pdf`;
    doc.save(fileName);
};
